<style scoped>
.dp-skeleton-buttons {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
  padding: 4px 5px;
  opacity: 0.75;
}
.dp-skeleton-button {
  border: none;
  border-radius: 0.5rem;
  display: inline-block;
  height: 32px;
  width: 85px;
  background: linear-gradient(90deg, #888f8f 25%, #565f5f 50%, #888f8f 75%);
  background-size: 200% 100%;
  animation: dp-loading 1.5s infinite;
}
@keyframes dp-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
<template>
  <div id="dp-spaces-header" ref="headerRef">
    <div class="dp-skeleton-buttons">
      <div class="dp-skeleton-button"></div>
      <div class="dp-skeleton-button"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { DoctoraliaAuthentication } from "@/types/DoctoraliaAuthentication";
import { HeaderMfeOptions } from "@/types/HeaderMfe";
import {
  buildHeaderOptions,
  expireStaleAnonymousToken,
  resolveDpSpacesSsoMount,
} from "@/utils/dp-spaces-sso-session";
import { toHeaderUserIdentity } from "@/utils/dp-spaces-user-identity";
import { loadRemote } from "@docplanner/remotejs-runtime";
import { onMounted, ref } from "vue";
import { useStore } from "vuex";

interface SpacesModule {
  mount: (selector: string, options: { shadowDOM: boolean }) => void;
}

function isSpacesModule(obj: unknown): obj is SpacesModule {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "mount" in obj &&
    typeof (obj as { mount?: unknown }).mount === "function"
  );
}

const store = useStore();

// Defines the events that the component can emit
const emit = defineEmits(["load", "error"]);

function safeReadCodeDpSpaces(): string | undefined {
  try {
    return sessionStorage.getItem("codeDpSpaces") ?? undefined;
  } catch {
    return undefined;
  }
}

function safeRemoveCodeDpSpaces(): void {
  try {
    sessionStorage.removeItem("codeDpSpaces");
  } catch {
    // degraded gracefully — sessionStorage unavailable
  }
}

// Defined reactive variables
const headerRef = ref<HTMLElement | null>(null);

console.log("[DP Spaces Header] Initializing");

// Define lifecycle hooks
onMounted(async () => {
  try {
    const header = await loadHeaderModule();
    header.mount("#dp-spaces-header", { shadowDOM: true });
    onHeaderToggle(false);
    console.log("[DP Spaces Header] Mounted");
    emit("load");
  } catch (e) {
    console.error("[DP Spaces Header] Error loading external module", e);
    onHeaderToggle(true);
    if (headerRef.value) {
      headerRef.value.innerHTML = "";
    }
    emit("error");
  }
});

function onHeaderToggle(isCollapsed: boolean) {
  headerRef.value?.parentElement?.classList.toggle("dp-collapsed", isCollapsed);
  store.commit("setDpSpacesCollapsed", isCollapsed);
}

async function loadHeaderModule(): Promise<SpacesModule> {
  const authenticationData: DoctoraliaAuthentication = await store.dispatch(
    "fetchDoctoraliaAuthentication"
  );

  let redirectURI: string;

  console.log("[DP Spaces Header] Current hostname:", window.location.hostname);
  console.log("[DP Spaces Header] Current pathname:", window.location.pathname);

  if (
    window.location.hostname === "app.feegow.com" ||
    window.location.hostname === "dasa.feegow.com" ||
    window.location.hostname === "demo.feegow.com" ||
    window.location.hostname === "app3.feegow.com" ||
    window.location.hostname === "app4.feegow.com" ||
    window.location.hostname === "franchising.feegow.com"
  ) {
    const trocouUnidade = document.userProperties?.trocouUnidadeNaSessao;
    console.log("[DP Spaces Header] trocouUnidadeNaSessao:", trocouUnidade);

    if (trocouUnidade === 1) {
      const currentPath = window.location.pathname.replace(/\/$/, "");
      console.log(
        "[DP Spaces Header] Unit change detected - preserving context:",
        currentPath
      );
      redirectURI = new URL(
        `${currentPath}/?P=Home&Pers=1`,
        `https://${window.location.hostname}/`
      ).toString();
      console.log(
        "[DP Spaces Header] Generated redirectURI for unit change:",
        redirectURI
      );
    } else {
      console.log(
        "[DP Spaces Header] First login or no unit change - using main path"
      );
      redirectURI = new URL(
        "main/?P=Home&Pers=1",
        `https://${window.location.hostname}/`
      ).toString();
      console.log(
        "[DP Spaces Header] Generated redirectURI for standard flow:",
        redirectURI
      );
    }
  } else {
    console.log("[DP Spaces Header] Using current href for other domains");
    redirectURI = window.location.href;
  }

  console.log("[DP Spaces Header] Final redirectURI:", redirectURI);

  const codeDpSpaces = safeReadCodeDpSpaces();

  // CON-1849: an /sso login hands the header the user's own token and entity
  // instead of an authCode, or the non-integrated header when those keys are
  // unusable. With no SSO key the legacy authCode rule applies. The mount's
  // options are merged over baseOptions by buildHeaderOptions.
  const ssoMount = resolveDpSpacesSsoMount(
    (key) => sessionStorage.getItem(key),
    Math.floor(Date.now() / 1000),
    authenticationData
  );
  console.log("[DP Spaces Header] Mode:", ssoMount.mode);
  expireStaleAnonymousToken(ssoMount, document);

  const baseOptions: HeaderMfeOptions = {
    locale: "br",
    softwareProvider: "feegow",
    clientId: authenticationData?.clientId,
    onHeaderToggle,
    userIdentity: toHeaderUserIdentity(authenticationData?.userIdentity),
    redirectURI,
    onGoToPmsMainPage: () =>
      (window.location.href = `https://${window.location.hostname}/`),
    isUserIntegrated: Boolean(authenticationData),
    onAuthCodeCleanup: safeRemoveCodeDpSpaces,
  };
  const options = buildHeaderOptions(baseOptions, ssoMount, codeDpSpaces);

  const remoteModule = await loadRemote({
    app: "integrations-patient-portal",
    name: "header",
    expose: "app",
    options,
  });

  if (!isSpacesModule(remoteModule)) {
    throw new Error("Loaded remote module is not a valid SpacesModule.");
  }

  return remoteModule;
}
</script>
